import { useApprovals, useProjectNotes, useRuns, useServices } from "../../lib/queries";
import { useCockpit } from "../../lib/state";
import { relativeTime } from "../../lib/format";
import { projectGradientCss } from "../../lib/projectColor";

interface ActivityEvent {
  id: string;
  ts: string;
  kind: "run" | "note" | "approval";
  label: string;
  project: string | null;
}

export default function Footer() {
  const { data: runs = [] } = useRuns();
  const { data: notes = [] } = useProjectNotes();
  const { data: approvals = [] } = useApprovals();
  const { data: services = [] } = useServices();
  const setHelpOpen = useCockpit((s) => s.setHelpOpen);

  const events: ActivityEvent[] = [
    ...runs.map<ActivityEvent>((r) => ({
      id: `run:${r.id}`,
      ts: r.ended_at ?? r.started_at,
      kind: "run",
      label: r.summary ?? `${r.agent} ${r.status}`,
      project: r.project,
    })),
    ...notes.map<ActivityEvent>((n) => ({
      id: `note:${n.id}`,
      ts: n.interpreted_by_agent_at ?? n.created_at,
      kind: "note",
      label: n.interpreted_by_agent_at
        ? `Interpreted: ${n.body.slice(0, 80)}`
        : `Note saved: ${n.body.slice(0, 80)}`,
      project: n.project,
    })),
    ...approvals.map<ActivityEvent>((a) => ({
      id: `appr:${a.id}`,
      ts: a.decided_at ?? a.requested_at,
      kind: "approval",
      label: `${a.action} (${a.status})`,
      project: a.project,
    })),
  ]
    .sort((a, b) => +new Date(b.ts) - +new Date(a.ts))
    .slice(0, 10);

  const monthlyCost = services.reduce((s, sv) => s + (sv.monthly_cost_usd ?? 0), 0);

  return (
    <footer
      className="aero-glass mx-3 mb-3 flex shrink-0 items-center gap-3 overflow-hidden px-4"
      style={{ height: 38, borderRadius: 18 }}
    >
      <span
        className="font-eyebrow shrink-0"
        style={{
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--hot-pink)",
        }}
      >
        ✦ Activity
      </span>
      <div className="flex flex-1 items-center gap-4 overflow-x-auto whitespace-nowrap">
        {events.length === 0 ? (
          <span
            className="text-xs"
            style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", color: "var(--plum)", opacity: 0.6 }}
          >
            no recent events — clean slate, darling
          </span>
        ) : (
          events.map((e) => (
            <span key={e.id} className="flex items-center gap-1.5 text-xs">
              <span
                className="glossy-radial-bubble"
                style={{ width: 8, height: 8, color: e.project ? "var(--hot-pink)" : "var(--lavender-deep)" }}
              />
              <span
                className="font-eyebrow"
                style={{ fontSize: 9, letterSpacing: "0.12em", color: "var(--plum)", opacity: 0.55 }}
              >
                {relativeTime(e.ts)}
              </span>
              {e.project ? (
                <span
                  className="glossy-project-pill"
                  style={{ background: projectGradientCss(e.project, 135), fontSize: 8, padding: "2px 7px" }}
                >
                  {e.project}
                </span>
              ) : null}
              <span style={{ color: "var(--ink)", fontWeight: 600 }}>{e.label.slice(0, 80)}</span>
            </span>
          ))
        )}
      </div>
      <span
        className="glossy-pill glossy-pill--ghost glossy-pill--small shrink-0"
      >
        ${Math.round(monthlyCost)}/mo
      </span>
      <button
        type="button"
        onClick={() => setHelpOpen(true)}
        className="shrink-0"
        title="Keyboard help"
      >
        <span className="kbd">?</span>
      </button>
    </footer>
  );
}
